// ============================================================================
// SplitsMatrix — situational splits matrix built from pitch-level events.
//
// Rows = split dimensions (Home/Away, vs hand, month, count, inning, and
// times-through-order for pitchers). Columns = rate stats. Every cell is
// colored relative to the player's OWN season baseline (top row), with
// direction-aware coloring (e.g. lower OPS-against is good for a pitcher).
//
// At-bats are reconstructed from the deduplicated pitch-event stream the same
// way ArsenalTab does it: an at-bat ends when isLastPitch !== false or the
// gameId changes. Pre-pitch count of the final pitch is carried from the
// previous pitch's POST-pitch balls/strikes (MLB API counts are post-pitch).
// ============================================================================
import React, { useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, useTheme, alpha, Chip,
} from '@mui/material';
import { GridOn } from '@mui/icons-material';

// ── At-bat result classification ────────────────────────────────────────────
// Order matters: check compound results (double_play, triple_play, sac_fly)
// before their substrings (double, triple, fly).
const classifyResult = (raw) => {
  const s = (raw || '').toLowerCase();
  if (!s) return null;
  if (s.includes('home_run') || s.includes('home run')) return { pa: 1, ab: 1, h: 1, hr: 1, tb: 4 };
  if (s.includes('strikeout') || s.includes('strike_out')) return { pa: 1, ab: 1, k: 1 };
  if (s.includes('walk')) return { pa: 1, bb: 1 };                     // walk + intent_walk
  if (s.includes('hit_by_pitch') || s.includes('hit by pitch')) return { pa: 1, hbp: 1 };
  if (s.includes('sac_fly') || s.includes('sac fly')) return { pa: 1, sf: 1 };
  if (s.includes('sac_bunt') || s.includes('sac bunt')) return { pa: 1 };
  if (s.includes('catcher_interf')) return { pa: 1 };
  if (s.includes('triple_play') || s.includes('double_play') || s.includes('double play')) return { pa: 1, ab: 1 };
  if (s.includes('triple')) return { pa: 1, ab: 1, h: 1, tb: 3 };
  if (s.includes('double')) return { pa: 1, ab: 1, h: 1, tb: 2 };
  if (s.includes('single')) return { pa: 1, ab: 1, h: 1, tb: 1 };
  if (s.includes('error')) return { pa: 1, ab: 1 };
  // field_out, force_out, fielders_choice, pop/line/fly/ground outs, other_out
  if (s.includes('out') || s.includes('choice')) return { pa: 1, ab: 1 };
  return null; // non-PA events (pickoffs, runner events, etc.)
};

// ── Aggregate a list of classified at-bats into a stat line ─────────────────
const makeLine = (abs) => {
  const t = { pa: 0, ab: 0, h: 0, hr: 0, bb: 0, k: 0, hbp: 0, sf: 0, tb: 0 };
  for (const a of abs) {
    const c = a.line;
    t.pa += c.pa || 0; t.ab += c.ab || 0; t.h += c.h || 0; t.hr += c.hr || 0;
    t.bb += c.bb || 0; t.k += c.k || 0; t.hbp += c.hbp || 0; t.sf += c.sf || 0; t.tb += c.tb || 0;
  }
  return {
    pa: t.pa,
    avg: t.ab > 0 ? t.h / t.ab : null,
    obp: t.pa > 0 ? (t.h + t.bb + t.hbp) / t.pa : null,
    slg: t.ab > 0 ? t.tb / t.ab : null,
    ops: (t.ab > 0 && t.pa > 0) ? ((t.h + t.bb + t.hbp) / t.pa) + (t.tb / t.ab) : null,
    hr: t.hr,
    kRate: t.pa > 0 ? (t.k / t.pa) * 100 : null,
    bbRate: t.pa > 0 ? (t.bb / t.pa) * 100 : null,
  };
};

const MONTH_LABELS = { 3: 'March', 4: 'April', 5: 'May', 6: 'June', 7: 'July', 8: 'August', 9: 'September', 10: 'October' };

const fmtAvg = (v) => {
  if (v == null || isNaN(v)) return '—';
  if (v >= 1) return v.toFixed(3);
  return v.toFixed(3).substring(1);
};
const fmtPct = (v) => (v == null || isNaN(v)) ? '—' : v.toFixed(1) + '%';

// Column definitions: spread = delta that maps to full color intensity.
// higherIsBetter is from the BATTER's point of view; flipped for pitchers.
const COLS = [
  { key: 'pa', label: 'PA', fmt: (v) => v ?? '—', colored: false },
  { key: 'avg', label: 'AVG', fmt: fmtAvg, colored: true, spread: 0.050, higherIsBetter: true },
  { key: 'obp', label: 'OBP', fmt: fmtAvg, colored: true, spread: 0.050, higherIsBetter: true },
  { key: 'slg', label: 'SLG', fmt: fmtAvg, colored: true, spread: 0.090, higherIsBetter: true },
  { key: 'ops', label: 'OPS', fmt: fmtAvg, colored: true, spread: 0.130, higherIsBetter: true, bold: true },
  { key: 'hr', label: 'HR', fmt: (v) => v ?? '—', colored: false },
  { key: 'kRate', label: 'K%', fmt: fmtPct, colored: true, spread: 7, higherIsBetter: false },
  { key: 'bbRate', label: 'BB%', fmt: fmtPct, colored: true, spread: 4.5, higherIsBetter: true },
];

const MIN_PA_FOR_COLOR = 10;

const SplitsMatrix = ({ pitchEvents, perspective = 'batter', season, gameTypeFilter }) => {
  const theme = useTheme();
  const isPitcher = perspective === 'pitcher';

  // ── Reconstruct at-bats from the pitch stream ──
  const atBats = useMemo(() => {
    const evts = Array.isArray(pitchEvents) ? pitchEvents : [];
    const filtered = (gameTypeFilter && gameTypeFilter !== 'all')
      ? evts.filter(e => (e.gt || 'R') === gameTypeFilter)
      : evts;

    const abs = [];
    let carryBalls = 0, carryStrikes = 0, lastGameId = null;
    for (const e of filtered) {
      if (e.gameId !== lastGameId) { carryBalls = 0; carryStrikes = 0; lastGameId = e.gameId; }
      const preBalls = carryBalls, preStrikes = carryStrikes;
      // Post-pitch count carried forward for the next pitch in this AB
      if (e.balls != null) carryBalls = e.balls;
      if (e.strikes != null) carryStrikes = Math.min(e.strikes, 2);

      if (e.isLastPitch !== false) {
        const line = classifyResult(e.atBatResult);
        if (line) {
          const month = e.gameDate ? parseInt(e.gameDate.split('-')[1], 10) : null;
          abs.push({
            line,
            homeAway: e.homeAway || null,
            hand: e.opponentHand || null,
            month,
            inning: e.inning || null,
            gameId: e.gameId,
            opponentName: e.opponentName || null,
            preBalls, preStrikes,
          });
        }
        carryBalls = 0; carryStrikes = 0;
      }
    }
    return abs;
  }, [pitchEvents, gameTypeFilter]);

  // ── Times-through-order tags (pitcher only) ──
  const ttoTagged = useMemo(() => {
    if (!isPitcher || atBats.length === 0) return null;
    const seen = {}; // gameId -> { batterName -> count }
    return atBats.map(a => {
      if (!a.gameId || !a.opponentName) return { ...a, tto: null };
      if (!seen[a.gameId]) seen[a.gameId] = {};
      const n = (seen[a.gameId][a.opponentName] || 0) + 1;
      seen[a.gameId][a.opponentName] = n;
      return { ...a, tto: n };
    });
  }, [atBats, isPitcher]);

  // ── Build rows ──
  const { baseline, groups } = useMemo(() => {
    const base = makeLine(atBats);
    const g = [];
    const push = (groupLabel, rows) => {
      const nonEmpty = rows.filter(r => r.line.pa > 0);
      if (nonEmpty.length > 0) g.push({ label: groupLabel, rows: nonEmpty });
    };

    // Location
    push('Location', [
      { label: 'Home', line: makeLine(atBats.filter(a => a.homeAway === 'home')) },
      { label: 'Away', line: makeLine(atBats.filter(a => a.homeAway === 'away')) },
    ]);

    // Handedness
    const handNoun = isPitcher ? 'B' : 'P';
    push('Handedness', [
      { label: `vs LH${handNoun}`, line: makeLine(atBats.filter(a => a.hand === 'L')) },
      { label: `vs RH${handNoun}`, line: makeLine(atBats.filter(a => a.hand === 'R')) },
    ]);

    // Month
    const months = [...new Set(atBats.map(a => a.month).filter(Boolean))].sort((a, b) => a - b);
    push('By Month', months.map(m => ({
      label: MONTH_LABELS[m] || `Month ${m}`,
      line: makeLine(atBats.filter(a => a.month === m)),
    })));

    // Count situation at final pitch (pre-pitch count)
    const hasCounts = atBats.some(a => a.preBalls > 0 || a.preStrikes > 0);
    if (hasCounts) {
      push('Count (result pitch)', [
        { label: 'Batter Ahead', line: makeLine(atBats.filter(a => a.preBalls > a.preStrikes)) },
        { label: 'Even Count', line: makeLine(atBats.filter(a => a.preBalls === a.preStrikes)) },
        { label: 'Batter Behind', line: makeLine(atBats.filter(a => a.preStrikes > a.preBalls)) },
        { label: 'Two Strikes', line: makeLine(atBats.filter(a => a.preStrikes === 2)) },
        { label: 'Three Balls', line: makeLine(atBats.filter(a => a.preBalls === 3)) },
      ]);
    }

    // Innings
    push('By Inning', [
      { label: 'Innings 1–3', line: makeLine(atBats.filter(a => a.inning >= 1 && a.inning <= 3)) },
      { label: 'Innings 4–6', line: makeLine(atBats.filter(a => a.inning >= 4 && a.inning <= 6)) },
      { label: 'Innings 7+', line: makeLine(atBats.filter(a => a.inning >= 7)) },
    ]);

    // Times through order (pitcher only)
    if (ttoTagged) {
      push('Times Through Order', [
        { label: '1st Time Thru', line: makeLine(ttoTagged.filter(a => a.tto === 1)) },
        { label: '2nd Time Thru', line: makeLine(ttoTagged.filter(a => a.tto === 2)) },
        { label: '3rd+ Time Thru', line: makeLine(ttoTagged.filter(a => a.tto >= 3)) },
      ]);
    }

    return { baseline: base, groups: g };
  }, [atBats, ttoTagged, isPitcher]);

  // ── Cell coloring vs own baseline ──
  const cellSx = (col, value, pa) => {
    if (!col.colored || value == null || baseline[col.key] == null) return {};
    if (pa < MIN_PA_FOR_COLOR) return {};
    const delta = value - baseline[col.key];
    // From the player's perspective: batter wants higherIsBetter as-is, pitcher flipped
    const better = isPitcher ? !col.higherIsBetter : col.higherIsBetter;
    const signed = better ? delta : -delta;
    const intensity = Math.min(Math.abs(signed) / col.spread, 1);
    if (intensity < 0.12) return {};
    const color = signed > 0 ? theme.palette.success.main : theme.palette.error.main;
    return { bgcolor: alpha(color, 0.07 + intensity * 0.25), borderRadius: 0.5 };
  };

  if (atBats.length === 0) return null;

  const statNoun = isPitcher ? 'Opponents batting against' : 'Batting';

  return (
    <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <GridOn sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
              Splits Matrix
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {statNoun} by situation, {season} — cells colored vs own season baseline
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Chip label="Better" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(theme.palette.success.main, 0.2), fontWeight: 600 }} />
            <Chip label="Worse" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(theme.palette.error.main, 0.2), fontWeight: 600 }} />
          </Box>
        </Box>

        <TableContainer>
          <Table size="small" sx={{ minWidth: 620 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', py: 0.75, px: 1.5, minWidth: 130, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 }}>
                  Split
                </TableCell>
                {COLS.map(c => (
                  <TableCell key={c.key} align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', py: 0.75, px: 1 }}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Baseline row */}
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <TableCell sx={{ fontWeight: 800, fontSize: '0.85rem', py: 0.8, px: 1.5, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                  Season {isPitcher ? '(against)' : ''}
                </TableCell>
                {COLS.map(c => (
                  <TableCell key={c.key} align="right" sx={{ fontWeight: 800, fontSize: '0.85rem', py: 0.8, px: 1 }}>
                    {c.fmt(baseline[c.key])}
                  </TableCell>
                ))}
              </TableRow>

              {groups.map(group => (
                <React.Fragment key={group.label}>
                  <TableRow>
                    <TableCell colSpan={COLS.length + 1} sx={{
                      py: 0.5, px: 1.5, fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.8,
                      textTransform: 'uppercase', color: 'text.secondary',
                      bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.03) : alpha('#000', 0.02),
                      borderBottom: 'none',
                    }}>
                      {group.label}
                    </TableCell>
                  </TableRow>
                  {group.rows.map(row => {
                    const lowSample = row.line.pa < MIN_PA_FOR_COLOR;
                    return (
                      <TableRow key={row.label} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600, py: 0.65, px: 1.5, pl: 2.5, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, whiteSpace: 'nowrap' }}>
                          {row.label}
                          {lowSample && (
                            <Tooltip title={`Small sample (${row.line.pa} PA) — not colored`} arrow>
                              <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'warning.main', fontSize: '0.65rem' }}>*</Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        {COLS.map(c => (
                          <TableCell key={c.key} align="right" sx={{
                            fontSize: '0.85rem', py: 0.65, px: 1,
                            fontWeight: c.bold ? 700 : 400,
                            ...cellSx(c, row.line[c.key], row.line.pa),
                          }}>
                            {c.fmt(row.line[c.key])}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ px: 2, py: 0.75, borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
            Derived from {atBats.length.toLocaleString()} plate appearances of pitch-by-pitch data.
            * = fewer than {MIN_PA_FOR_COLOR} PA. Count rows use the count when the at-bat ended.
            {isPitcher && ' Times-through-order tracks each batter\u2019s Nth PA vs this pitcher per game.'}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SplitsMatrix;
